import React, { useState, useEffect, useRef, useMemo } from "react";
import { formatDateFromTimestamp, formatDateTimeUtcFromTimestamp } from "../utils/dateUtils";
import FifoDetailRow from "./FifoDetailRow";
import SaleDetailRow from "./SaleDetailRow";
import NftTransformDetailRow from "./NftTransformDetailRow.jsx";
import { useLanguage } from "../contexts/LanguageContext.jsx";
import { t, getTaxCategoryLabel } from "../utils/translations";
import ExportButton from "./ExportButton";
import MaskedEurCell from "./MaskedEurCell";
import {
  getPreviewTaxIssueKind,
  PreviewTaxIssueBadge,
  PreviewTaxIssueCell,
} from "./PreviewTaxIssue";
import { LayoutDashboard } from "lucide-react";
import { cn, tableTheme as T, analysisTone } from "./ui/analysisTableTheme.js";
import UnifiedTableEmptyState, { computeTableEmptyMetrics } from "./UnifiedTableEmptyState.jsx";
import {
  pickSectionTotals,
  currencySymbolOf,
  pickFiatPrice,
  pickFiatBuy,
  pickFiatSell,
  pickFiatTotal,
  pickFiatGrossProfit,
  pickFiatTaxable,
  pickFiatNonTaxable,
  isBlockedTaxRow,
  blockedTaxLabel,
} from "../utils/fiatFields";
import { isUkCountry } from "../utils/ukTaxUi.js";
import VaultXTypeLabel from "./VaultXTypeLabel.jsx";
import { getVaultXDisplay } from "../utils/vaultxDisplay.js";
import {
  shouldMaskPreviewEurRow,
  shouldShowPreviewSeparatorAfterRow,
} from "../utils/previewMode";
import PreviewTableNotice from "./PreviewTableNotice";
import PreviewModeSeparator from "./PreviewModeSeparator";
import TaxTreatmentValue from "./TaxTreatmentValue.jsx";
import { rowMatchesTaxYear, resolveDisplayTaxYearLabel } from "../utils/taxYear.js";
import { buildNftTaxTableSectionData } from "../utils/taxTableSectionData.js";
import { resolveNftCollectionDisplayName } from "../utils/nftCollections.js";
import {
  buildNftTaxTableDisplayRows,
  isNftTaxPrimaryDisplayRow,
} from "../../shared/nftAcquisitionLedger.js";
import { NFT_TRANSFORM_E2E_BUILD_MARKER } from "../../shared/nftTransformBuildMarker.js";
import { normalizeSearchText } from "../utils/classificationLabel.js";
import {
  nftRowClass,
  isNftSalePaymentRow,
  isNftAcquisitionPaymentRow,
  isNftMarketplacePayoutReviewRow,
  isNftTransformRow,
  isNftCostBasisUnknown,
  isNftKnownZeroCostBasis,
  nftPurchaseDisplayValue,
  nftProfitDisplayValue,
} from "../utils/nftCostBasis.js";

const HeaderIconBadge = ({ children, className = "" }) => (
  <div className={cn(T.headerMediaBadge, className)}>
    <div className="absolute inset-0 bg-gradient-to-br from-white via-white/85 to-slate-50/80" />
    <div className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
      <div className="absolute -top-2 -left-3 h-5 w-12 rotate-12 bg-white/50 blur-md" />
    </div>
    <div className="relative z-10 flex items-center justify-center">
      {children}
    </div>
  </div>
);

// 🧪 Preview: Helper für sichere Zahl-Formatierung
const fmtFixed = (v, d = 2) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(d) : "—";
};

const firstPresentFinite = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const number = Number(value);
    if (Number.isFinite(number)) return number;
  }
  return null;
};

// 🚀 Helper-Funktion für Timestamp-Normalisierung (wie in VTHO-Tabelle)
const toSec = (ts) => {
  // Zahl in ms -> sec, Zahl in sec bleibt sec, ISO-String wird geparst
  if (typeof ts === 'string') return Math.floor(new Date(ts).getTime() / 1000);
  const n = Number(ts);
  return n > 1e12 ? Math.floor(n / 1000) : n;
};

// Helper: Soll diese Transaktion im PDF alle Details zeigen?
const isImportantFifoTx = (tx) => {
  const type = (tx.type || tx.event || "").toLowerCase();
  const classification = (tx.classification || "").toLowerCase();

  return (
    type === "swap_out" ||
    classification === "swap_out" ||
    type === "sale" ||
    classification === "sale" ||
    type === "sale_payment" ||
    classification === "sale_payment" ||
    type === "nft_mint" ||
    classification === "nft_mint" ||
    type === "nft_galaxy_mint" ||
    classification === "nft_galaxy_mint" ||
    type === "nft_marketplace_purchase_payment" ||
    classification === "nft_marketplace_purchase_payment" ||
    type === "nft_transform" ||
    classification === "nft_transform"
  );
};

// Eindeutige, stabile ID für jede Mint/Sale-Zeile (Join-Key zu FIFO_Legs)
const makeSaleId = (r) => {
  const ts = r.timestamp ? (r.timestamp > 1e12 ? r.timestamp : r.timestamp * 1000) : 0;
  const tid = r.tokenId ?? '';
  return `${r.hash || 'nohash'}-${ts}-${tid}`;
};

// Gewichtet nach zugeordneten Verkaufserlösen (sellEUR)
const holdingStats = (legs = [], { skipHoldingDays = false } = {}) => {
  if (!legs.length || skipHoldingDays) return { min: null, max: null, wavg: null };
  const days = legs
    .map((l) => Number(l.holdingDays))
    .filter((d) => Number.isFinite(d));
  if (!days.length) return { min: null, max: null, wavg: null };
  const min = Math.min(...days);
  const max = Math.max(...days);
  const totalSell = legs.reduce((s, l) => s + Number(l.sellEUR || 0), 0);
  const wavg = totalSell > 0
    ? legs.reduce((s, l) => s + Number(l.holdingDays || 0) * Number(l.sellEUR || 0), 0) / totalSell
    : days.reduce((a,b)=>a+b,0) / days.length; // Fallback: einfacher Mittelwert
  return { min, max, wavg: Math.round(wavg) };
};

export default function NftTaxTable({ 
  year,
  displayTaxYear,
  datasetId = null,
  nftDataFromProps = [], // FIFO/tax events
  nftAcquisitionRowsFromProps = [], // one row per acquired NFT identity
  nftTotalsFromProps = {}, // 🚀 NEU: Backend-Totals direkt als Props
  onTotalProfitCalculated, 
  onDataReady, 
  isEditMode = false,
  selectedCountry = 'DE',
  // 🚀 LEGACY: Alte Props für Kompatibilität (werden ignoriert)
  nftApiData = [], 
  nftFifoData = [], 
  vip180Data = [], 
  userAddress, 
  manualClassifications = {}, 
  vetVthoData = [],
  nonTaxableInflows = {},
  nonTaxableSales = {},
  onNonTaxableInflowChange = () => {},
  onNonTaxableSaleChange = () => {},
  // 🔧 NEU: Props für SaleDetailRow
  onManualNftDataChange = () => {},
  onSaveNftTransformFmv = null,
  manualNftData = {},
  pdfExportMode = false,
  enableExportCallback = true,
  isPreviewMode = false,
  previewMaskedTotalEUR = null,
  previewMaskedTotals = null,
}) {
  const { language } = useLanguage();
  const tone = analysisTone.indigo;
  const primaryTableWrapClass = "v3-primary-table-shell v3-primary-table-shell--violet";
  const primaryTableHeadClass = "v3-primary-table-head v3-primary-table-head--violet";

  const cc = String(selectedCountry || "").toUpperCase();
  const isUS = cc === "US" || cc === "USA" || cc.includes("UNITED STATES");
  const isUK = isUkCountry(selectedCountry);
  const totalsMeta = useMemo(
    () => pickSectionTotals(nftTotalsFromProps || {}, selectedCountry),
    [nftTotalsFromProps, selectedCountry]
  );
  const currencyCode = totalsMeta.currency;
  const currencySymbol = currencySymbolOf(currencyCode);
  // 🚀 NEU: Einfacher State für Display-Only
  const [data, setData] = useState({ rows: [], totals: { totalCost: 0, totalValue: 0 } });
  const effectiveYear = String(year || ""); // Kein Fallback auf aktuelles Jahr
  const yearLabel = resolveDisplayTaxYearLabel(year, selectedCountry, displayTaxYear);

  const pdfSectionData = useMemo(() => {
    if (!pdfExportMode) return null;
    return buildNftTaxTableSectionData(
      nftDataFromProps,
      nftTotalsFromProps,
      effectiveYear,
      selectedCountry,
      { acquisitionRows: nftAcquisitionRowsFromProps }
    );
  }, [
    pdfExportMode,
    nftDataFromProps,
    nftAcquisitionRowsFromProps,
    nftTotalsFromProps,
    effectiveYear,
    selectedCountry,
  ]);

  const activeRows = pdfSectionData?.rows ?? data.rows;
  const activeTotals = pdfSectionData?.totals ?? data.totals;

  const processedRef = useRef(false);
  
  // UI State (nur für Anzeige)
  const [sortKey, setSortKey] = useState("timestamp");
  const [sortDirection, setSortDirection] = useState("desc");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [tokenFilter, setTokenFilter] = useState("ALL");
  const [expandedIndex, setExpandedIndex] = useState(null);

  const toggleRow = (index) => {
    if (pdfExportMode) return;
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  // Flat table: one row per NFT identity; batch parents are expanded into acquisition rows.
  useEffect(() => {
    
    if (!Array.isArray(nftDataFromProps)) {
      return;
    }

    const taxRows = nftDataFromProps
      .map((r) => {
        let cls = nftRowClass(r);
        if (cls === "mint") cls = "nft_mint";
        if (cls === "sale") cls = "sale_payment";
        if (cls === "galaxy_mint") cls = "nft_galaxy_mint";
        return {
          ...r,
          rawClassification: r.classification,
          classification: cls || r.classification,
        };
      })
      .filter(isNftTaxPrimaryDisplayRow);

    const yearFilteredTax = taxRows.filter((r) => {
      const isTransformParent = nftRowClass(r) === "nft_transform";
      const ts =
        Number(
          (isTransformParent ? r.nftTransformBurnTs : null) ||
            r.timestamp ||
            r.nftTransformMintTs ||
            r.nftTransformBurnTs ||
            r.ts ||
            0
        ) || 0;
      if (!ts) return false;
      if (!effectiveYear) return false;
      return rowMatchesTaxYear(
        { ...r, timestamp: ts },
        selectedCountry,
        Number(effectiveYear)
      );
    });

    const yearFilteredAcq = (Array.isArray(nftAcquisitionRowsFromProps)
      ? nftAcquisitionRowsFromProps
      : []
    ).filter((r) => {
      if (!r.timestamp && !r.mintTimestamp && !r.paymentTimestamp) return false;
      if (!effectiveYear) return false;
      return rowMatchesTaxYear(
        {
          ...r,
          timestamp: r.timestamp || r.mintTimestamp || r.paymentTimestamp,
        },
        selectedCountry,
        Number(effectiveYear)
      );
    });

    const rows = buildNftTaxTableDisplayRows(yearFilteredTax, yearFilteredAcq);

    const m = pickSectionTotals(nftTotalsFromProps || {}, selectedCountry);
    const totals = {
      disposalGainEUR: m.disposalGain,
      disposalLossEUR: m.disposalLoss,
      disposalNetEUR: m.disposalNet,
      disposalEUR: m.disposalNet,
      incomeEUR: m.otherIncome,
      totalCost: m.disposalNet,
      totalValue: m.disposalNet,
      disposalShortTermNetFiat: m.disposalShortTermNet,
      disposalLongTermNetFiat: m.disposalLongTermNet,
    };

    setData({ rows, totals });
    processedRef.current = true;

  }, [
    nftDataFromProps,
    nftAcquisitionRowsFromProps,
    nftTotalsFromProps,
    effectiveYear,
    selectedCountry,
  ]);

  // CSV/Export - Vollständige Backend-Daten exportieren
  useEffect(() => {
    if (!onDataReady || isPreviewMode || !enableExportCallback) return;
    
    const exportable = (data.rows || []).map(r => {
      // 🔧 FIX: Korrekte Datumsformatierung (Datum Uhrzeit Format statt ISO 8601)
      const formatDate = (timestamp) => {
        if (!timestamp) return '';
        const ts = timestamp > 1e12 ? timestamp : timestamp * 1000;
        const csvLocale =
          String(selectedCountry || 'DE').toUpperCase().includes('US')
            ? 'en-US'
            : 'de-DE';

        return new Date(ts).toLocaleString(csvLocale, {
          day: '2-digit',
          month: '2-digit', 
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        });
      };

      // 🔧 FIX: NFT-Name Encoding-Problem beheben (Bindestrich)
      const fixNftName = (name) => {
        if (!name) return null;
        // Ersetze problematische Zeichen mit korrektem Bindestrich
        return name.replace(/â€"/g, '–').replace(/â€"/g, '—').replace(/â€™/g, "'").replace(/â€œ/g, '"').replace(/â€/g, '"');
      };

      const isTransform = nftRowClass(r) === "nft_transform";
      const transformPrice = firstPresentFinite(
        r.resultFmvFiat,
        r.nftTransformResultFmvFiat
      );
      const transformTotal = firstPresentFinite(
        r.totalExchangeValueFiat,
        r.disposalProceedsTotalFiat,
        r.resultFmvFiat,
        r.nftTransformResultFmvFiat,
        r.sourceBundleFmvFiat,
        r.nftTransformSourceBundleFmvFiat,
        r.sourceFmvTotalFiat
      );
      const saleValue = isTransform
        ? transformTotal
        : pickFiatSell(r) ?? pickFiatTotal(r) ?? 0;
      const costBasisUnknown =
        isNftCostBasisUnknown(r) && !isNftKnownZeroCostBasis(r);
      const purchaseValue = costBasisUnknown ? null : nftPurchaseDisplayValue(r);
      const profitValue = costBasisUnknown
        ? null
        : (nftProfitDisplayValue(r) ??
          (purchaseValue != null ? saleValue - purchaseValue : null));
      const saleValueEUR = saleValue;
      const purchasePriceEUR = purchaseValue;
      const taxFreeBasisEUR = r.isFullyTaxFree
        ? saleValueEUR
        : (pickFiatNonTaxable(r) ?? r.nonTaxableProfitEUR ?? 0);
      const profitEUR = profitValue;
      
      // 🔧 FALLBACK: Einzelner holdingDays-Wert für Rückwärtskompatibilität (erster Leg oder 0)
      const holdingDays = isUK
        ? null
        : (r.fifoLegs?.[0]?.holdingDays ??
          (r.fifoLegs?.length > 0
            ? Math.floor((r.timestamp - (r.fifoLegs[0]?.timestamp ?? r.timestamp)) / (60 * 60 * 24))
            : 0));
      
      // 🔧 IMPROVED: Generiere stabile Sale-ID und Haltefristen-Statistiken
      const sale_id = makeSaleId(r);
      const { min: holding_days_min, max: holding_days_max, wavg: holding_days_wavg } = holdingStats(
        r.fifoLegs || [],
        { skipHoldingDays: isUK }
      );
      const fifo_legs_count = r.fifoLegs?.length ?? 0;
      
      // 🔧 FIX: Bestimme taxable Status
      const taxable = costBasisUnknown
        ? null
        : (r.taxableProfitEUR !== null
          ? (r.taxableProfitEUR !== 0)
          : (r.fifoLegs?.[0]?.isTaxable ?? false));

      // 🔧 LOGIC: holdingDays für NFT Mints (Käufe) und Sales unterschiedlich
      const isMint = isNftAcquisitionPaymentRow(r);
      const isSale = isNftSalePaymentRow(r);

      return {
        // ---- CSV MULTI-EXPORT START ----
        type: r.classification, // nft_mint | nft_galaxy_mint | sale_payment
        hash: r.hash || '',
        nftName: fixNftName(r.nftName), // 🔧 FIX: Encoding-Problem
        tokenId: r.tokenId ?? '', // falls im Datensatz vorhanden
        token: isTransform ? "NFT" : r.token || 'VET',
        amount: isTransform ? r.transformDisplayAmount || "3 → 1" : r.amount,
        euroPrice: isTransform
          ? transformPrice
          : pickFiatPrice(r) ?? r.euroPrice ?? 0,
        date: formatDate(r.timestamp), // lokal formatiert, DE
        saleValueEUR: saleValueEUR,
        purchasePriceEUR: purchasePriceEUR,
        profitEUR: profitEUR,
        taxable: taxable,
        taxableProfitEUR: r.taxableProfitEUR ?? null,
        nonTaxableProfitEUR: r.nonTaxableProfitEUR ?? null,
        isFullyTaxFree: r.isFullyTaxFree ?? null,
        taxFreeBasisEUR: taxFreeBasisEUR, // = Erlöse wenn voll steuerfrei, sonst nicht-steuerpflichtiger Gewinn
        holdingDaysMin: isUK ? null : (isMint ? holding_days_min : (isSale ? holdingDays : null)),
        holdingDaysMax: isUK ? null : (isMint ? holding_days_max : null),
        fifoLegsCount: fifo_legs_count, // (Legacy)
        
        // ---- CSV MULTI-EXPORT END ----
        
        // Backend-berechnete Steuerdaten (für Debugging)
        grossBuyEUR: r.grossBuyEUR ?? null,
        grossSellEUR: r.grossSellEUR ?? null,
        grossProfitEUR: r.grossProfitEUR ?? null,
        
        // FIFO-Detail (aus den ersten Legs extrahieren)
        firstLegBuyEUR: r.fifoLegs?.[0]?.buyEUR ?? null,
        firstLegSellEUR: r.fifoLegs?.[0]?.sellEUR ?? null,
        firstLegHoldingDays: r.fifoLegs?.[0]?.holdingDays ?? null,
        firstLegTaxable: r.fifoLegs?.[0]?.isTaxable ?? null,
        
        // Aggregierte FIFO-Info
        allLegsHoldingDays: r.fifoLegs ? r.fifoLegs.map(leg => leg.holdingDays).join(';') : null,
        allLegsTaxable: r.fifoLegs ? r.fifoLegs.map(leg => leg.isTaxable).join(';') : null
      };
    });
    onDataReady(exportable);
  }, [data.rows, selectedCountry, isPreviewMode, onDataReady, enableExportCallback]);

  useEffect(() => {
    onTotalProfitCalculated?.({
      totalCost: data.totals.totalCost,
      totalValue: data.totals.totalValue,
      total: data.totals.totalCost
    });
  }, [data.totals]); // ✅ onTotalProfitCalculated entfernt um Endlosschleife zu vermeiden

  // 🚀 NEU: Filterung und Sortierung (wie VetTaxTable)
  const filtered = useMemo(() => {
    let filtered = activeRows || [];
    
    // Suchfilter
    if (search) {
      const searchLower = search.toLowerCase();
      const searchNormalized = normalizeSearchText(search);
      const matchesNormalized = (value) =>
        searchNormalized !== '' &&
        normalizeSearchText(value).includes(searchNormalized);
      filtered = filtered.filter(tx => 
        (tx.classification || '').toLowerCase().includes(searchLower) ||
        matchesNormalized(tx.classification || tx.type || '') ||
        matchesNormalized(resolveNftCollectionDisplayName(tx) || '') ||
        matchesNormalized(tx.nftName || '') ||
        matchesNormalized(tx.collection || '') ||
        String(tx.tokenId ?? '').toLowerCase().includes(searchLower.replace(/^#/, "")) ||
        (tx.hash || '').toLowerCase().includes(searchLower) ||
        (tx.token || '').toLowerCase().includes(searchLower) ||
        (tx.amount || 0).toString().includes(searchLower) ||
        (tx.euroPrice || 0).toString().includes(searchLower) ||
        (tx.euroAmount || 0).toString().includes(searchLower) ||
        (tx.timestamp ? new Date((tx.timestamp > 1e12 ? tx.timestamp : tx.timestamp * 1000)).toLocaleDateString() : '').includes(searchLower)
      );
    }
    
    // Typ-Filter
    if (typeFilter !== "ALL") {
      filtered = filtered.filter(tx => (tx.classification || '').toLowerCase() === typeFilter.toLowerCase());
    }
    
    // Token-Filter
    if (tokenFilter !== "ALL") {
      filtered = filtered.filter(tx => (tx.token || '').toLowerCase() === tokenFilter.toLowerCase());
    }
    
    return filtered;
  }, [activeRows, search, typeFilter, tokenFilter]);

  // Sortierung (wie VetTaxTable)
  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    
    return [...filtered].sort((a, b) => {
      let aVal = a[sortKey];
      let bVal = b[sortKey];
      
      // Spezielle Behandlung für verschiedene Datentypen
      if (sortKey === 'timestamp') {
        aVal = aVal > 1e12 ? aVal : aVal * 1000;
        bVal = bVal > 1e12 ? bVal : bVal * 1000;
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      } else {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortKey, sortDirection]);

  useEffect(() => {
    setExpandedIndex(null);
  }, [sortKey, sortDirection, search, tokenFilter, typeFilter, sorted.length]);

  const emptyMetrics = useMemo(
    () =>
      computeTableEmptyMetrics({
        allRows: nftDataFromProps,
        yearRows: activeRows,
        filteredRows: filtered,
        effectiveYear,
      }),
    [nftDataFromProps, activeRows, filtered, effectiveYear]
  );

  const totals = activeTotals || { totalCost: 0, totalValue: 0 };
  
  // Verfügbare Tokens für Filter sammeln
  const availableTokens = useMemo(() => {
    const tokens = new Set();
    activeRows.forEach(tx => {
      if (tx.token) tokens.add(tx.token);
    });
    return Array.from(tokens).sort();
  }, [activeRows]);

const handleSort = (key) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
  } else {
    setSortKey(key);
      setSortDirection('asc');
  }
};

const getArrow = (key) => {
  if (key !== sortKey) return "";
  return sortDirection === "asc" ? " ↑" : " ↓";
};

const isNftReviewRow = (row = {}) => {
  return Boolean(
    isNftMarketplacePayoutReviewRow(row) ||
    isBlockedTaxRow(row) ||
    (isNftTransformRow(row) &&
      (row.reviewRequired === true || row.taxTreatment === "review_required")) ||
    (isNftSalePaymentRow(row) &&
      isNftCostBasisUnknown(row) &&
      !isNftKnownZeroCostBasis(row))
  );
};

const renderTaxableProfitCell = (row = {}, { mobile = false } = {}) => {
  const review = isNftReviewRow(row);

  if (review) {
    return (
      <span
        className={
          mobile
            ? "inline-flex items-center justify-end rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900"
            : "inline-flex items-center justify-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900"
        }
      >
        {blockedTaxLabel(row, language)}
      </span>
    );
  }

  if (isNftTransformRow(row) && row.noRealization === true) {
    return (
      <span
        className={
          mobile
            ? "inline-flex items-center justify-end rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700"
            : "inline-flex items-center justify-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700"
        }
      >
        {language === "en" ? "No realization" : "Keine Realisierung"}
      </span>
    );
  }

  const value =
    pickFiatTaxable(row) ??
    row.taxableProfitFiat ??
    row.taxableProfitEUR ??
    row.taxableProfit;

  if (value === null || value === undefined || value === "") {
    return <span className="font-mono text-slate-400">—</span>;
  }

  return (
    <TaxTreatmentValue
      row={row}
      language={language}
      selectedCountry={selectedCountry}
      treatment="disposal"
      align={mobile ? "right" : "center"}
      formattedValue={`${currencySymbol} ${fmtFixed(value, 5)}`}
    />
  );
};

  // 🚀 NEU: Einfache Backend-Daten-Verarbeitung (keine komplexe Filterung mehr nötig)

  // 🚀 NEU: Einfache Backend-Daten-Verarbeitung (keine komplexe Filterung mehr nötig)

  // 🚀 NEU: Einfache Backend-Daten-Verarbeitung (keine komplexe Filterung mehr nötig)

  const renderNftThead = () => (
    <thead className={cn(T.thead, primaryTableHeadClass, pdfExportMode && "print:bg-white")}>
            <tr>
              <th
                className={cn(T.th, "text-center", !pdfExportMode && "cursor-pointer")}
                onClick={!pdfExportMode ? () => handleSort("type") : undefined}
              >
                <span className={T.thInnerCenter}>
                  <span className={cn(T.thLabel, "v3-th-label-fix")}>
                    {t("type", language)}
                    {!pdfExportMode ? getArrow("type") : null}
                  </span>
                </span>
              </th>

              <th
                className={cn(T.th, "text-center", !pdfExportMode && "cursor-pointer")}
                onClick={!pdfExportMode ? () => handleSort("hash") : undefined}
              >
                <span className={T.thInnerCenter}>
                  <span className={cn(T.thLabel, "v3-th-label-fix")}>
                    {t("txHashColumn", language)}
                    {!pdfExportMode ? getArrow("hash") : null}
                  </span>
                </span>
              </th>

              <th
                className={cn(T.th, "text-center", !pdfExportMode && "cursor-pointer")}
                onClick={!pdfExportMode ? () => handleSort("nftName") : undefined}
              >
                <span className={T.thInnerCenter}>
                  <span className={cn(T.thLabel, "v3-th-label-fix")}>
                    {t("nftName", language)}
                    {!pdfExportMode ? getArrow("nftName") : null}
                  </span>
                </span>
              </th>

              <th
                className={cn(T.th, "text-center", !pdfExportMode && "cursor-pointer")}
                onClick={!pdfExportMode ? () => handleSort("tokenId") : undefined}
              >
                <span className={T.thInnerCenter}>
                  <span className={cn(T.thLabel, "v3-th-label-fix")}>
                    {t("tokenId", language)}
                    {!pdfExportMode ? getArrow("tokenId") : null}
                  </span>
                </span>
              </th>

              <th
                className={cn(T.th, "text-center", !pdfExportMode && "cursor-pointer")}
                onClick={!pdfExportMode ? () => handleSort("token") : undefined}
              >
                <span className={T.thInnerCenter}>
                  <span className={cn(T.thLabel, "v3-th-label-fix")}>
                    {t("currency", language)}
                    {!pdfExportMode ? getArrow("token") : null}
                  </span>
                </span>
              </th>

              <th
                className={cn(T.th, "text-center", !pdfExportMode && "cursor-pointer")}
                onClick={!pdfExportMode ? () => handleSort("amount") : undefined}
              >
                <span className={T.thInnerCenter}>
                  <span className={cn(T.thLabel, "v3-th-label-fix")}>
                    {t("amount", language)}
                    {!pdfExportMode ? getArrow("amount") : null}
                  </span>
                </span>
              </th>

              <th
                className={cn(T.th, "text-center", !pdfExportMode && "cursor-pointer")}
                onClick={!pdfExportMode ? () => handleSort("euroPrice") : undefined}
              >
                <span className={T.thInnerCenter}>
                  <span className={cn(T.thLabel, "v3-th-label-fix")}>
                    {t("price", language)}
                    {!pdfExportMode ? getArrow("euroPrice") : null}
                  </span>
                  <span className={cn(T.thSub, "v3-th-sub-fix")}>{currencyCode}</span>
                </span>
              </th>

              <th
                className={cn(T.th, "text-center", !pdfExportMode && "cursor-pointer")}
                onClick={!pdfExportMode ? () => handleSort("totalEUR") : undefined}
              >
                <span className={T.thInnerCenter}>
                  <span className={cn(T.thLabel, "v3-th-label-fix")}>
                    {t("total", language)}
                    {!pdfExportMode ? getArrow("totalEUR") : null}
                  </span>
                  <span className={cn(T.thSub, "v3-th-sub-fix")}>{currencyCode}</span>
                </span>
              </th>

              <th
                className={cn(T.th, "text-center", !pdfExportMode && "cursor-pointer")}
                onClick={!pdfExportMode ? () => handleSort("taxableProfit") : undefined}
              >
                <span className={T.thInnerCenter}>
                  <span className={cn(T.thLabel, "v3-th-label-fix")}>
                    {t("taxableProfitShort", language)}
                    {!pdfExportMode ? getArrow("taxableProfit") : null}
                  </span>
                  <span className={cn(T.thSub, "v3-th-sub-fix")}>({currencyCode})</span>
                </span>
              </th>

              <th
                className={cn(T.th, "text-center", !pdfExportMode && "cursor-pointer")}
                onClick={!pdfExportMode ? () => handleSort("timestamp") : undefined}
              >
                <span className={T.thInnerCenter}>
                  <span className={cn(T.thLabel, "v3-th-label-fix")}>
                    {pdfExportMode ? (language === "en" ? "Date (UTC)" : "Datum (UTC)") : t("date", language)}
                    {!pdfExportMode ? getArrow("timestamp") : null}
                  </span>
                </span>
              </th>
            </tr>
          </thead>
  );

  const renderNftFooter = () => (
    sorted.length > 0 ? (
              <>
                <tr className={cn("font-semibold border-t", tone.footerPrimary, "print:bg-gray-200 print:text-black")}>
                  <td colSpan={8} className="px-6 py-4 text-right print:text-black">
                    {getTaxCategoryLabel("disposal", selectedCountry, language)} ({t("nftPurchasesAndSales", language)}) –{" "}
                    {language === "en" ? `Sum ${yearLabel}` : `Summe Steuerjahr ${yearLabel}`}:
                  </td>
                  <td className="px-6 py-4 font-mono text-center print:text-black">
                    {isPreviewMode ? (
                      <MaskedEurCell
                        value={previewMaskedTotals?.disposal || `${currencySymbol} **.**`}
                        fullWidth
                        currencySymbol={currencySymbol}
                        currencyCode={currencyCode}
                        language={language}
                      />
                    ) : (
                      <>
                        {currencySymbol} {fmtFixed(totals.disposalNetEUR, 2)}
                      </>
                    )}
                  </td>
                  <td className="px-6 py-4" />
                </tr>
                {(totals.incomeEUR !== 0 || (isPreviewMode && previewMaskedTotals?.hasOther)) && (
                  <tr className={cn("font-semibold border-t", tone.footerSecondary, "print:bg-gray-200 print:text-black")}>
                    <td colSpan={8} className="px-6 py-4 text-right print:text-black">
                      {getTaxCategoryLabel("other", selectedCountry, language)} –{" "}
                      {language === "en" ? `Sum ${yearLabel}` : `Summe Steuerjahr ${yearLabel}`}:
                    </td>
                    <td className="px-6 py-4 font-mono text-center print:text-black">
                      {isPreviewMode ? (
                        <MaskedEurCell
                          value={previewMaskedTotals?.other || `${currencySymbol} **.**`}
                          fullWidth
                          currencySymbol={currencySymbol}
                          currencyCode={currencyCode}
                          language={language}
                        />
                      ) : (
                        <>
                          {currencySymbol} {fmtFixed(totals.incomeEUR, 2)}
                        </>
                      )}
                    </td>
                    <td className="px-6 py-4" />
                  </tr>
                )}
              </>
            ) : null
  );

  const renderNftDesktopRow = (tx, index) => {
          const maskRowEur = shouldMaskPreviewEurRow(index, sorted.length, isPreviewMode);
          const cls = nftRowClass(tx);
              const isSalePayment = isNftSalePaymentRow(tx);
              const isMint = isNftAcquisitionPaymentRow(tx);
              const isMarketplaceReview = isNftMarketplacePayoutReviewRow(tx);
              const isTransform = isNftTransformRow(tx);
              const isGalaxyMint = cls === "nft_galaxy_mint";
              const isMarketplacePurchase = cls === "nft_marketplace_purchase_payment";
              const acquisitionLabel = isMarketplacePurchase
                ? (language === "en" ? "NFT Purchase" : "NFT-Kauf")
                : (isGalaxyMint ? "Galaxy Mint" : "NFT Mint");

              const transformLabel = (() => {
                const kind = String(tx.nftTransformKind || "").toLowerCase();
                if (kind.includes("thorhead")) {
                  return language === "en" ? "Thorheads ReMint" : "Thorheads ReMint";
                }
                if (kind.includes("vemugs")) {
                  return language === "en" ? "VeMugs Melt" : "VeMugs Melt";
                }
                return language === "en" ? "NFT Transform" : "NFT-Transform";
              })();

              const ts = tx.timestamp || 0;
              const transformPrice = isTransform
                ? firstPresentFinite(
                    tx.resultFmvFiat,
                    tx.nftTransformResultFmvFiat,
                    tx.unitPriceFiat
                  )
                : null;
              const transformTotal = isTransform
                ? firstPresentFinite(
                    tx.totalExchangeValueFiat,
                    tx.disposalProceedsTotalFiat,
                    tx.resultFmvFiat,
                    tx.nftTransformResultFmvFiat,
                    tx.sourceBundleFmvFiat,
                    tx.nftTransformSourceBundleFmvFiat,
                    tx.sourceFmvTotalFiat,
                    tx.amountFiat
                  )
                : null;
              const priceEUR = isTransform
                ? transformPrice
                : (pickFiatPrice(tx) ?? tx.euroPrice) || 0;
              const totalEUR = isTransform
                ? transformTotal
                : (pickFiatTotal(tx) ?? tx.euroAmount) || 0;

              const fifoRows = tx.fifoLegs || tx.matchedInflows || [];

              const hasFifoDetails = fifoRows.length > 0;
              const isImportant = isImportantFifoTx(tx) || isSalePayment || isTransform;
              const isMintOrSale = isMint || isSalePayment;

              const reviewRow = isNftReviewRow(tx);

              const shouldShowButton = !pdfExportMode && (isMintOrSale || isTransform);
              const isExpanded = expandedIndex === index;

              const showDetails =
                pdfExportMode
                  ? (
                      isTransform ||
                      (
                        (hasFifoDetails ||
                          isSalePayment) &&
                        isImportant
                      )
                    )
                  : expandedIndex === index &&
                    (
                      isMintOrSale ||
                      isTransform ||
                      (
                        hasFifoDetails &&
                        !isPreviewMode
                      )
                    );

              const previewIssueKind = getPreviewTaxIssueKind(tx);
              const showPreviewIssueRow = isPreviewMode && !!previewIssueKind;

              return (
                <React.Fragment key={String(tx.hash) + "-" + String(tx.tokenId ?? "") + "-" + index}>
                  {pdfExportMode && showDetails ? (
                    <tr className="pdf-nft-row-head">
                      <th className={cn(T.th, "text-center")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-form")}>{t("type", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-form")}>{t("txHashColumn", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-form")}>{t("nftName", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-form")}>{t("tokenId", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-form")}>{t("currency", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-form")}>{t("amount", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-form")}>{t("price", language)}</span>
                          <span className={cn(T.thSub, "v3-th-sub-form")}>{currencyCode}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-form")}>{t("total", language)}</span>
                          <span className={cn(T.thSub, "v3-th-sub-form")}>{currencyCode}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-form")}>{t("taxableProfitShort", language)}</span>
                          <span className={cn(T.thSub, "v3-th-sub-form")}>({currencyCode})</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-form")}>{pdfExportMode ? (language === "en" ? "Date (UTC)" : "Datum (UTC)") : t("date", language)}</span>
                        </span>
                      </th>
                    </tr>
                  ) : null}
                  <tr
                    className={cn(
                      T.tr,
                      "group pdf-tx-main-row transition-colors duration-150",
                      "print:hover:bg-transparent",
                      reviewRow
                        ? "bg-amber-50/70 hover:bg-amber-100/60"
                        : "hover:bg-slate-50/80"
                    )}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {isMint ? (
                          shouldShowButton ? (
                            <button
                              className="text-sm text-blue-700 hover:text-blue-900 hover:underline transition-colors duration-200"
                              onClick={() => toggleRow(index)}
                              disabled={pdfExportMode}
                              title={
                                isPreviewMode
                                  ? language === "en"
                                    ? "Preview: fiat amounts locked, details still visible"
                                    : "Preview: Fiat-Beträge gesperrt, Details sichtbar"
                                  : undefined
                              }
                            >
                              <span className="inline-flex items-center gap-1">
                                <span aria-hidden>{isExpanded ? "▼" : "▶"}</span>
                                <span>{acquisitionLabel}</span>
                              </span>{" "}
                            </button>
                          ) : (
                            <span className="text-gray-700 font-medium">
                              {acquisitionLabel}
                            </span>
                          )
                        ) : isMarketplaceReview ? (
                          <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900">
                            {language === "en"
                              ? "Marketplace payout review"
                              : "WoV Marketplace Payout — Receipt nötig"}
                          </span>
                        ) : isTransform ? (
                          shouldShowButton ? (
                            <button
                              className="text-sm text-blue-700 hover:text-blue-900 hover:underline transition-colors duration-200"
                              onClick={() => toggleRow(index)}
                              disabled={pdfExportMode}
                            >
                              <span className="inline-flex items-center gap-1">
                                <span aria-hidden>{isExpanded ? "▼" : "▶"}</span>
                                <span>{transformLabel}</span>
                              </span>
                            </button>
                          ) : (
                            <span className="text-gray-700 font-medium">{transformLabel}</span>
                          )
                        ) : isSalePayment ? (
                          shouldShowButton ? (
                            <button
                              className="text-sm text-blue-700 hover:text-blue-900 hover:underline transition-colors duration-200"
                              onClick={() => toggleRow(index)}
                              disabled={pdfExportMode}
                              title={
                                isPreviewMode
                                  ? language === "en"
                                    ? "Preview: fiat amounts locked, details still visible"
                                    : "Preview: Fiat-Beträge gesperrt, Details sichtbar"
                                  : undefined
                              }
                            >
                              <span className="inline-flex items-center gap-1">
                                <span aria-hidden>{isExpanded ? "▼" : "▶"}</span>
                                <span>Sale Payment</span>
                              </span>{" "}
                            </button>
                          ) : (
                            <span className="text-gray-700 font-medium">Sale Payment</span>
                          )
                        ) : getVaultXDisplay(tx.classification) ? (
                          <VaultXTypeLabel classification={tx.classification} />
                        ) : (
                          <span className="text-gray-800 print:text-black">
                            {tx.classification || 'Unknown'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 w-[18%] text-center text-gray-700 print:text-black">
                      <div className="break-all max-w-[200px] whitespace-normal" title={tx.hash || '—'}>
                        {tx.hash || '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4 w-[16%] text-center text-gray-800 print:text-black">
                      <div className="break-words max-w-[200px] whitespace-normal" title={resolveNftCollectionDisplayName(tx) || tx.nftName || '—'}>
                        {(() => {
                          const display = resolveNftCollectionDisplayName(tx) || tx.nftName || '';
                          return display
                            ? display.replace(/â€"/g, '–').replace(/â€"/g, '—').replace(/â€™/g, "'").replace(/â€œ/g, '"').replace(/â€/g, '"')
                            : '—';
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4 w-[12%] text-center text-gray-800 print:text-black">
                      <div className="break-all max-w-[120px] whitespace-normal" title={tx.tokenId || tx.relatedMintTokenId || '—'}>
                        {isTransform
                          ? (tx.nftTransformResultTokenId ??
                              tx.nftTransformAcquisition?.tokenId ??
                              tx.tokenId ??
                              tx.relatedMintTokenId ??
                              '—')
                          : (tx.tokenId || tx.relatedMintTokenId || '—')}
                      </div>
                    </td>
                    <td className="px-6 py-4 w-[8%] text-center text-gray-800 print:text-black">
                      {isTransform ? "NFT" : (tx.token || 'VET').toUpperCase()}
                    </td>
                    <td className="px-6 py-4 w-[8%] text-center text-gray-900 print:text-black">
                      {isTransform
                        ? tx.transformDisplayAmount || "3 → 1"
                        : Number.isFinite(Number(tx.amount))
                          ? Number(tx.amount).toFixed(2)
                          : "—"}
                    </td>
                    {showPreviewIssueRow ? (
                      <PreviewTaxIssueCell
                        language={language}
                        issueKind={previewIssueKind}
                        colSpan={3}
                        className="print:text-black"
                      />
                    ) : (
                      <>
                        <td className="px-6 py-4 w-[8%] text-center text-gray-900 print:text-black">
                          {maskRowEur ? (
                            <MaskedEurCell fullWidth currencySymbol={currencySymbol} currencyCode={currencyCode} language={language} />
                          ) : isTransform && priceEUR == null ? (
                            <span className="font-mono text-slate-400">—</span>
                          ) : (
                            <>{currencySymbol} {fmtFixed(priceEUR, 5)}</>
                          )}
                        </td>
                        <td className="px-6 py-4 w-[10%] text-center text-gray-900 print:text-black">
                          {maskRowEur ? (
                            <MaskedEurCell fullWidth currencySymbol={currencySymbol} currencyCode={currencyCode} language={language} />
                          ) : isTransform && totalEUR == null ? (
                            <span className="font-mono text-slate-400">—</span>
                          ) : (
                            <>{currencySymbol} {fmtFixed(totalEUR, 5)}</>
                          )}
                        </td>
                        <td className="px-6 py-4 w-[10%] text-center text-gray-900 print:text-black">
                          {maskRowEur ? (
                            <MaskedEurCell fullWidth currencySymbol={currencySymbol} currencyCode={currencyCode} language={language} />
                          ) : (
                            renderTaxableProfitCell(tx)
                          )}
                        </td>
                      </>
                    )}
                    <td className="px-6 py-4 w-[10%] text-center text-gray-600 print:text-black">
                      {ts > 0
                        ? (pdfExportMode
                            ? formatDateTimeUtcFromTimestamp(ts)
                            : formatDateFromTimestamp(ts))
                        : "—"}
                    </td>
                  </tr>

                  {showDetails && isTransform && (
                    <NftTransformDetailRow
                      tx={tx}
                      colSpan={10}
                      currencySymbol={currencySymbol}
                      currencyCode={currencyCode}
                      language={language}
                      pdfExportMode={pdfExportMode}
                      onSaveResultFmv={
                        datasetId && !pdfExportMode && onSaveNftTransformFmv
                          ? onSaveNftTransformFmv
                          : null
                      }
                    />
                  )}

                  {showDetails && isMintOrSale && (
                     isSalePayment ? (
                       <SaleDetailRow 
                         saleTx={tx}
                         fullData={nftDataFromProps}
                         isEditMode={isEditMode}
                         nonTaxableSales={nonTaxableSales}
                         onNonTaxableChange={onNonTaxableSaleChange}
                         selectedCountry={selectedCountry}
                         onManualNftDataChange={onManualNftDataChange}
                         manualNftData={manualNftData}
                         isPreviewMode={isPreviewMode}
                         maskRowEur={maskRowEur}
                         pdfExportMode={pdfExportMode}
                         language={language}
                       />
                     ) : (
                       <FifoDetailRow 
                         tx={tx}
                         inflows={fifoRows}
                         colSpan={10}
                         isEditMode={isEditMode}
                         nonTaxableInflows={nonTaxableInflows}
                         onNonTaxableChange={onNonTaxableInflowChange}
                         selectedCountry={selectedCountry}
                         isPreviewMode={isPreviewMode}
                         maskRowEur={maskRowEur}
                         pdfExportMode={pdfExportMode}
                         language={language}
                       />
                     )
                  )}
                  <PreviewModeSeparator
                    show={shouldShowPreviewSeparatorAfterRow(index, sorted.length, isPreviewMode)}
                    colSpan={10}
                    language={language}
                    currencyCode={currencyCode}
                  />
                  {pdfExportMode && index < sorted.length - 1 ? (
                    <tr className="pdf-tx-separator" aria-hidden="true">
                      <td colSpan={10} className="pdf-tx-separator-cell" />
                    </tr>
                  ) : null}
                </React.Fragment>
              );};


  // Return JSX
  return (
    <section
      data-nft-transform-build-marker={NFT_TRANSFORM_E2E_BUILD_MARKER}
      className={cn(
        T.shell,
        "print:mt-0 report-section",
        pdfExportMode && "rounded-none border-0 bg-white shadow-none ring-0"
      )}
    >
      <div className={cn(T.header, T.headerRail, tone.rail)}>
        <div className={T.headerInner}>
          <div className={T.headerMain}>
            <span className={T.eyebrow}>
              <LayoutDashboard className={cn("h-3.5 w-3.5", tone.eyebrow)} />
              {t("taxYear", language)} {yearLabel}
            </span>
            <h2 className={T.title}>
              {t("nft", language)} {t("taxTransactions", language)}
            </h2>
            <p className={T.subtitle}>
              {getTaxCategoryLabel("disposal", selectedCountry, language)}
              {" · "}
              {language === "en"
                ? "NFT mints, sales and related taxable events"
                : "NFT-Mints, Verkäufe und zugehörige steuerliche Vorgänge"}
            </p>
          </div>
          {!pdfExportMode && (
            <div className={T.headerAsidePills}>
              <span className={cn(T.headerChipSoft, "text-violet-700 border-violet-200 bg-violet-50/80")}>
                <span className="h-2 w-2 rounded-full bg-violet-500" />
                <span>NFT</span>
              </span>

              <HeaderIconBadge className="print:bg-white print:shadow-none print:ring-gray-300">
                <img src="/earth.png" alt="NFT" className="object-contain" draggable={false} />
              </HeaderIconBadge>
            </div>
          )}
        </div>
      </div>

      {!pdfExportMode && (
        <div className={T.toolbarWrap}>
          <div className={cn(T.toolbar, "flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between")}>
            <div className="flex-1 min-w-0">
              <input
                type="text"
                placeholder={`🔍 ${t("search", language)} ${t("type", language)}, ${t("token", language)}, ${t("transactionHash", language)}, ${pdfExportMode ? (language === "en" ? "Date (UTC)" : "Datum (UTC)") : t("date", language)}...`}
                className={T.input}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className={T.countPill}>
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                <span className="font-semibold text-slate-800">{sorted.length}</span>
                <span>{t("transactions", language)}</span>
              </span>
              <select className={T.select} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="ALL">🔎 {t("allTypes", language)}</option>
                <option value="nft_mint">🎨 NFT Mint</option>
                <option value="nft_galaxy_mint">🌌 Galaxy Mint</option>
                <option value="nft_marketplace_purchase_payment">🛒 NFT-Kauf</option>
                <option value="nft_transform">
                  🔄 ReMint / Melt
                </option>
                <option value="sale_payment">💰 Sale Payment</option>
              </select>
              <select className={T.select} value={tokenFilter} onChange={(e) => setTokenFilter(e.target.value)}>
                <option value="ALL">🪙 {t("allTokens", language)}</option>
                {availableTokens.map((token) => (
                  <option key={token} value={token}>
                    {token}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      <PreviewTableNotice
        isPreviewMode={isPreviewMode}
        totalRows={sorted.length}
        language={language}
        currencyCode={currencyCode}
      />

      <div className={cn(T.tableBlock, "hidden md:block print:block")}>
        <div className={cn(T.tableWrap, primaryTableWrapClass)}>
        {sorted.length === 0 ? (
          <table
            className={cn(
              T.table,
              "min-w-[1480px] table-fixed print:min-w-0",
              pdfExportMode && "v3-pdf-tx-blocks v3-pdf-no-thead-repeat"
            )}
          >
            {renderNftThead()}
            <tbody className={T.tbody}>
              <tr>
                <td colSpan={10} className="px-6 py-10">
                  <UnifiedTableEmptyState
                    language={language}
                    year={yearLabel}
                    scopeLabel="NFT"
                    totalCount={emptyMetrics.totalCount}
                    yearCount={emptyMetrics.yearCount}
                    otherYearsCount={emptyMetrics.otherYearsCount}
                    hiddenByFilterCount={emptyMetrics.hiddenByFilterCount}
                    inTable
                  />
                </td>
              </tr>
            </tbody>
          </table>
        ) : (
          <table
            className={cn(
              T.table,
              "min-w-[1480px] table-fixed print:min-w-0",
              pdfExportMode && "v3-pdf-tx-blocks v3-pdf-no-thead-repeat"
            )}
          >
            {renderNftThead()}
            <tbody className={T.tbody}>
              {sorted.map(renderNftDesktopRow)}
              {renderNftFooter()}
            </tbody>
          </table>
        )}
        </div>
    </div>
    </section>
  );
}
          
